'use client';

import { useQuery } from '@tanstack/react-query';
import { gatewayService } from '@/lib/api/services/gateway';
import { DEFAULT_CONNECTION, type ConnectionInfo } from '@/components/shell/simple/constants';

/**
 * MCP/API/SDK connection coordinates from the api-gateway.
 *
 * Always returns a usable value: the gateway's env-configured details once
 * loaded, the hardcoded defaults until then (or if the gateway is unreachable).
 * `isLoaded` distinguishes "real gateway data" from the fallback.
 */
export function useConnectionInfo(): { connection: ConnectionInfo; isLoaded: boolean } {
  const { data } = useQuery({
    queryKey: ['gateway-connection-info'],
    queryFn: ({ signal }) => gatewayService.getConnectionInfo(signal),
    staleTime: 5 * 60_000,
    retry: 1,
    // This query is intentionally non-fatal: when the gateway is unreachable we
    // fall back to DEFAULT_CONNECTION below. Opt out of the global throwOnError
    // policy (QueryProvider) so a gateway outage degrades gracefully instead of
    // bubbling to the root error boundary and taking down the entire page.
    throwOnError: false,
  });
  return { connection: data ?? DEFAULT_CONNECTION, isLoaded: !!data };
}
